import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLORS = ["#D96B43", "#4A7C59", "#E5A93C", "#3A86FF", "#9333EA", "#DB2777", "#059669", "#0EA5E9"];

export default function ChildDialog({ open, onOpenChange, child }) {
    const { refreshChildren, setActiveChildId } = useApp();
    const [name, setName] = useState("");
    const [color, setColor] = useState(COLORS[0]);
    const [username, setUsername] = useState("");
    const [pin, setPin] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setName(child?.name || "");
            setColor(child?.avatar_color || COLORS[0]);
            setUsername(child?.username || "");
            setPin("");
        }
    }, [open, child]);

    const save = async () => {
        if (!name.trim()) return;
        setBusy(true);
        try {
            const payload = {
                name: name.trim(),
                avatar_color: color,
                username: username.trim().toLowerCase(),
            };
            if (pin.trim()) payload.pin = pin.trim();
            if (child?.id) {
                await api.put(`/children/${child.id}`, payload);
                toast.success("Filho atualizado");
            } else {
                if (!pin.trim()) {
                    toast.error("Defina um PIN para o filho");
                    setBusy(false);
                    return;
                }
                const { data } = await api.post("/children", payload);
                setActiveChildId(data.id);
                toast.success("Filho adicionado");
            }
            await refreshChildren();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao salvar");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!child?.id) return;
        if (!window.confirm(`Remover ${child.name}? Isso apaga tarefas e check-ins dele.`)) return;
        setBusy(true);
        try {
            await api.delete(`/children/${child.id}`);
            toast.success("Removido");
            await refreshChildren();
            onOpenChange(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="child-dialog">
                <DialogHeader>
                    <DialogTitle>{child?.id ? "Editar filho" : "Adicionar filho"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Nome</Label>
                        <Input
                            data-testid="child-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex: Lucas"
                            className="mt-1.5"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Apelido para login</Label>
                            <Input
                                data-testid="child-username-input"
                                value={username}
                                onChange={(e) => setUsername(e.target.value.replace(/\s/g, "").toLowerCase())}
                                placeholder="ex: lucas"
                                className="mt-1.5"
                            />
                        </div>
                        <div>
                            <Label>PIN (4 dígitos)</Label>
                            <Input
                                data-testid="child-pin-input"
                                value={pin}
                                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                placeholder={child?.id ? "manter atual" : "1234"}
                                type="password"
                                inputMode="numeric"
                                className="mt-1.5"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Cor do avatar</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    data-testid={`child-color-${c.replace("#", "")}`}
                                    onClick={() => setColor(c)}
                                    className={`h-8 w-8 rounded-full ${color === c ? "ring-2 ring-offset-2 ring-slate-800" : ""}`}
                                    style={{ backgroundColor: c }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
                    {child?.id ? (
                        <Button variant="destructive" onClick={remove} disabled={busy} data-testid="child-delete-button">
                            Remover
                        </Button>
                    ) : <span />}
                    <Button
                        onClick={save}
                        disabled={busy || !name.trim()}
                        className="bg-[#D96B43] hover:bg-[#C25933] text-white rounded-full"
                        data-testid="child-save-button"
                    >
                        Salvar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
